import { CardGroupCard, CardIconsData, CardVariant, Color, ContentTableItem, IconName, UseState } from '@elearning-platform/ui';
import { useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { downloadFile } from '../api/requests';
import { AppConfig } from '../appConfig';
import {
  FavouriteItemType,
  KnowledgeBaseViewMode,
  LessonStatus,
  LessonTableColumnName,
  RouteNameEnum,
  ThumbnailAward,
  ThumbnailType,
  ViewImage,
} from '../enums';
import { downloadAll } from '../helpers/zipDownload';
import { Thumbnail, UseAuth, UseFavouriteItems, UseLessonCard, UseLessonCardOptions, UseRedirectTo } from '../interfaces';

import { useAuth } from './useAuth.hook';
import { useFavouriteItems } from './useFavouriteItems.hook';
import { useRedirectTo } from './useRedirectTo.hook';

export const useLessonCard: (options?: UseLessonCardOptions) => UseLessonCard = (options?: UseLessonCardOptions): UseLessonCard => {
  const { withoutFavoriteButton }: UseLessonCardOptions | undefined = options ?? {};
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [itemToShowOnSidePanel, setItemToShowOnSidePanel]: UseState<Thumbnail | undefined> = useState<Thumbnail | undefined>();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { t }: TransProps<never> = useTranslation();
  const { addItemAsync, removeItemAsync }: UseFavouriteItems = useFavouriteItems();
  const { appSettings }: UseAuth = useAuth();
  const viewMode: KnowledgeBaseViewMode
    = appSettings?.knowledgeBaseViewMode ?? KnowledgeBaseViewMode.Grid;

  const getFavouriteItemType: (itemType: ThumbnailType) => FavouriteItemType = (itemType: ThumbnailType): FavouriteItemType => {
    switch (itemType) {
      case ThumbnailType.Contents:
        return FavouriteItemType.ContentGroup;
      case ThumbnailType.Files:
        return FavouriteItemType.FileGroup;
      case ThumbnailType.Items:
        return FavouriteItemType.Competence;
      default:
        return FavouriteItemType.Content;
    }
  };

  const getCardImage: (item: Thumbnail) => string = (item: Thumbnail): string => {
    if (item.items?.length) {
      if (item.type === ThumbnailType.Files) {
        return '/files.jpg';
      }

      return item.items[0].image ? `${AppConfig.content}${item.items[0].image}` : '';
    } else if (item.type === ThumbnailType.File) {
      switch (item.url?.split('.').pop()) {
        case 'png':
          return ViewImage.PngCover;
        case 'jpg':
          return ViewImage.JpgCover;
        case 'docx':
          return ViewImage.DocCover;
        case 'pdf':
          return ViewImage.PdfCover;
        case 'xlsx':
          return ViewImage.XlsCover;
        case 'ppt':
          return ViewImage.PptCover;
      }
    }

    return item.image ? `${AppConfig.content}${item.image}` : '';
  };

  const getAwardIconColor: (award: ThumbnailAward) => Color = (award: ThumbnailAward): Color => {
    switch (award) {
      case ThumbnailAward.Gold:
        return Color.Gold;
      case ThumbnailAward.Silver:
        return Color.Silver;
      case ThumbnailAward.Bronze:
        return Color.Bronze;
      case ThumbnailAward.Completed:
        return Color.Green900;
      default: {
        const _: never = award;

        return Color.Green900;
      }
    }
  };

  const handleOnCardClick: (item: Thumbnail) => void = (item: Thumbnail): void => {
    if ([ThumbnailType.Files, ThumbnailType.Contents].includes(item.type)) {
      setIsSidePanelOpen(true);
      setItemToShowOnSidePanel(item);
      redirect({ hash: item.id.toString(), replace: true });
    } else if ([ThumbnailType.Lesson, ThumbnailType.LessonSlideScroll].includes(item.type)) {
      redirect({ route: RouteNameEnum.KnowledgeBaseLesson, params: { id: item.id } });
    } else if (item.type === ThumbnailType.Video) {
      redirect({ route: RouteNameEnum.VideoPlayer, params: { id: item.id } });
    }
  };

  const getLessonIcons: (item: Thumbnail) => CardIconsData[] = (item: Thumbnail): CardIconsData[] => {
    const awardIcon: CardIconsData[] = item.award
      ? [{ name: IconName.Award, color: getAwardIconColor(item.award), tooltipText: t('competence.app.hooks.lessonCard.passed') }]
      : [];

    switch (item.statusKey) {
      case LessonStatus.NotStarted:
        return [
          { name: IconName.Circle, color: Color.Brand300, tooltipText: t('competence.app.hooks.lessonCard.notStarted') },
          ...awardIcon,
        ];
      case LessonStatus.InProgress:
        return [
          { name: IconName.Time20s, color: Color.Brand300, tooltipText: t('competence.app.hooks.lessonCard.inProgress') },
          ...awardIcon,
        ];
      case LessonStatus.Finished:
        return [
          { name: IconName.CheckmarkCircle, color: Color.Green900, tooltipText: t('competence.app.hooks.lessonCard.completed') },
          ...awardIcon,
        ];
      case LessonStatus.ValidateTo:
        return [
          { name: IconName.CheckmarkCircle, color: Color.Green900, tooltipText: item.status ?? '' },
          ...awardIcon,
        ];
      case LessonStatus.Failed:
        return [
          { name: IconName.RemoveCircleOutlined, color: Color.Red300, tooltipText: t('competence.app.lessonCard.failed') },
          ...awardIcon,
        ];
      default:
        return [];
    }
  };

  const transformThumbnailIntoCardData: (item: Thumbnail) => CardGroupCard = (item: Thumbnail): CardGroupCard => ({
    onCardClick: (): void => handleOnCardClick(item),
    onDownloadAsync: item.type === ThumbnailType.Files
      ? (): Promise<void> => downloadAll(item.name, item.items)
      : item.type === ThumbnailType.File
        ? (): Promise<void> => new Promise((resolve: () => void): Promise<void> => downloadFile(item.id).then(resolve))
        : undefined,
    itemsInListNumber: item.items?.length,
    title: item.name,
    category: item.category,
    tags: [
      ...(item.required ? [t('competence.app.hooks.lessonCard.required')] : []),
      ...(item.cyclical ? [t('competence.app.hooks.lessonCard.cyclical')] : []),
    ],
    icons: getLessonIcons(item),
    image: getCardImage(item),
    progress: item.progress && item.progress * 100,
    onIsLikedChange: withoutFavoriteButton ? undefined : (): Promise<void> => new Promise(
      (resolve: () => void): Promise<void> => item.favorite
        ? removeItemAsync({ type: getFavouriteItemType(item.type), id: item.id }).then(resolve)
        : addItemAsync({ type: getFavouriteItemType(item.type), id: item.id }).then(resolve)
    ),
    initialIsLiked: !!item.favorite,
    shouldOpenInNewTab: true,
    variant: viewMode === KnowledgeBaseViewMode.List ? CardVariant.Horizontal : CardVariant.Vertical,
    description: item.description,
  });

  const transformThumbnailIntoTableData: (item: Thumbnail) => ContentTableItem = (item: Thumbnail): ContentTableItem => ({
    data: [
      {
        type: 'image',
        value: getCardImage(item),
        itemsInListNumber: item.items?.length,
        name: LessonTableColumnName.Image,
        progress: item.progress
          ? item.progress * 100
          : undefined,
      },
      { type: 'title', value: item.name, contentType: item.category, name: LessonTableColumnName.Title },
      {
        type: 'label',
        value: {
          list: [
            ...(item.required ? [t('competence.app.hooks.lessonCard.required')] : []),
            ...(item.cyclical ? [t('competence.app.hooks.lessonCard.cyclical')] : []),
          ],
          colorsData: {
            textColor: Color.Gray600,
            backgroundColor: Color.White,
            borderColor: Color.Gray300,
          },
        },
        name: LessonTableColumnName.Type,
      },
      { icons: getLessonIcons(item), type: 'icons', name: LessonTableColumnName.Status },
      {
        type: 'action',
        value: [
          ...item.description ? [{
            actionType: 'displayDescription' as const,
            description: item.description,
          }] : [],
          {
            actionType: 'toggleFavourite',
            onIsLikedChange: (): Promise<void> => new Promise(
              (resolve: () => void): Promise<void> => item.favorite
                ? removeItemAsync({ type: getFavouriteItemType(item.type), id: item.id }).then(resolve)
                : addItemAsync({ type: getFavouriteItemType(item.type), id: item.id }).then(resolve)
            ),
          },
          {
            actionType: 'download',
            onDownloadAsync: item.type === ThumbnailType.Files
              ? (): Promise<void> => downloadAll(item.name, item.items)
              : item.type === ThumbnailType.File
                ? (): Promise<void> => new Promise((resolve: () => void): Promise<void> => downloadFile(item.id).then(resolve))
                : undefined,
            linkData: item.type === ThumbnailType.File
              ? { externalUrl: item.url, fileName: item.name }
              : undefined,
          },
        ],
        name: LessonTableColumnName.Actions,
      },
    ],
    onCardClick: (): void => handleOnCardClick(item),
    initialIsLiked: !!item.favorite,
    type: 'lessonItem',
  });

  return {
    getCardImage,
    handleOnCardClick,
    isSidePanelOpen,
    itemToShowOnSidePanel,
    transformThumbnailIntoCardData,
    setIsSidePanelOpen,
    transformThumbnailIntoTableData,
    setItemToShowOnSidePanel,
  };
};
